"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// Gate geometry ported from the app's animated-gate.tsx (two pillars + arch).
const PILLAR_LENGTH = 63 - 35.5; // 27.5
const ARCH_LENGTH = Math.PI * 20;
const EASE = [0.22, 1, 0.36, 1] as const;

interface GateGlyphProps {
  size?: number;
  className?: string;
  /** Draw the gate on (pillars then arch). */
  animated?: boolean;
  /** Purple glow drop-shadow. */
  glow?: boolean;
}

/**
 * The moongate "gate" mark — two pillars and an arch, stroke-drawn — matching
 * the app's animated gate. Color comes from `currentColor` (set via text-*).
 */
export function GateGlyph({ size = 24, className, animated = true, glow = true }: GateGlyphProps) {
  const draw = (length: number, delay: number, duration: number) => ({
    strokeDasharray: length,
    initial: animated ? { strokeDashoffset: length } : false,
    animate: { strokeDashoffset: 0 },
    transition: { duration, delay, ease: EASE },
  });

  return (
    <svg
      viewBox="10 8 55 63"
      width={size}
      height={size * (63 / 55)}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="moongate"
      className={cn(glow && "drop-shadow-[0_0_8px_rgba(168,85,247,0.45)]", className)}
    >
      <motion.line
        x1={17.5}
        y1={35.5}
        x2={17.5}
        y2={63}
        stroke="currentColor"
        strokeWidth={6.5}
        strokeLinecap="round"
        {...draw(PILLAR_LENGTH, 0.2, 0.4)}
      />
      <motion.line
        x1={57.5}
        y1={63}
        x2={57.5}
        y2={35.5}
        stroke="currentColor"
        strokeWidth={6.5}
        strokeLinecap="round"
        {...draw(PILLAR_LENGTH, 0.45, 0.4)}
      />
      <motion.path
        d="M 17.5 35.5 A 20 20 0 0 0 57.5 35.5"
        stroke="currentColor"
        strokeWidth={6.5}
        strokeLinecap="round"
        fill="none"
        {...draw(ARCH_LENGTH, 0.85, 0.55)}
      />
    </svg>
  );
}
